import { Button, Stack, Typography } from '@mui/material';
import { CloseCircle } from 'assets';
import {
  AVAILABLE_PACKAGE_ITEM_TYPE,
  PURCHASED_PACKAGE_FROM,
} from 'models/credits';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { useAppDispatch } from 'store/hook';
import { setSelectedPackId } from 'store/reducers/credits';
import { setOpenBuyCredits } from 'store/reducers/payments';
import ModalUI from '../../common/ModalUI';
import {
  ButtonWrapper,
  PackageNameWrapper,
  PackageWrapper,
  PopularTag,
  PricingWrapper,
} from './styles';

interface PackageCreditItemProps {
  data?: AVAILABLE_PACKAGE_ITEM_TYPE;
  isPayWithCredit?: boolean;
}

interface SmallPopupWarningProps {
  open: boolean;
  onClose: () => void;
}

const SmallPopupWarning = ({ open, onClose }: SmallPopupWarningProps) => {
  return (
    <ModalUI
      open={open}
      onClose={onClose}
      hideCloseButton
      PaperProps={{
        sx: { width: 400, height: 'unset', margin: 3, borderRadius: 2 },
      }}
    >
      <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
        <CloseCircle color="primary" sx={{ width: 55, height: 55 }} />

        <Stack spacing={1} alignItems={'center'} justifyContent={'center'}>
          <Typography variant="Web_Title_18" color="primary.main">
            Something went wrong 😟
          </Typography>
          <Typography variant="Web_Label_14" textAlign={'center'}>
            Cannot purchase a different package. Please use up or top up your
            existing package to continue enjoying our services!
          </Typography>
        </Stack>
        <Button variant="contained" onClick={onClose} sx={{ width: 250 }}>
          Back
        </Button>
      </Stack>
    </ModalUI>
  );
};

export const PackageCreditItem = ({
  data,
  isPayWithCredit = false,
}: PackageCreditItemProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openWarning, setOpenWarning] = useState<boolean>(false);

  const type = isPayWithCredit
    ? PURCHASED_PACKAGE_FROM.JOB
    : PURCHASED_PACKAGE_FROM.CREDIT;

  const handleClose = () => {
    setOpenWarning(false);
  };

  const handlePurchase = () => {
    if (!data.is_can_purchase) {
      setOpenWarning(true);
    } else {
      handleClose();
      dispatch(setSelectedPackId(data.id));
      dispatch(setOpenBuyCredits(false));
      navigate({
        pathname: APP_ROUTES.CREDITS.PAYMENT.to,
        search: `?type=${type}`,
      });
    }
  };

  return (
    <Stack position="relative" flexWrap="wrap">
      {data.tag && <PopularTag>{data.tag}</PopularTag>}
      <PackageWrapper
        $tagExist={!!data.tag}
        $isCanPurchase={data.is_can_purchase}
      >
        <PackageNameWrapper>
          <Typography className="package-name">{data.name}</Typography>
          <Typography className="package-description">
            {data.description}
          </Typography>
        </PackageNameWrapper>
        <PricingWrapper>
          <Typography className="total-hours">${data.price}</Typography>
          <Typography className="per-hour">{`~$${data.parent_rate}/hour`}</Typography>
        </PricingWrapper>
        <ButtonWrapper>
          <Button variant={'contained'} fullWidth onClick={handlePurchase}>
            Buy
          </Button>
        </ButtonWrapper>
      </PackageWrapper>
      <SmallPopupWarning open={openWarning} onClose={handleClose} />
    </Stack>
  );
};

export default PackageCreditItem;
